import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import { Button } from '@factorialco/deprecated-design-system'
import { Dropdown } from '@factorialco/f0-react/dist/experimental'
import {
  ContractDetailDeleteContractVersionDocument,
  ContractVersionPageContractVersionFragment,
  ContractVersionsPageDocument,
  EmployeeProfilePageDocument,
} from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { getContractVersionSchedule } from 'modules/contracts/lib/graphqlContractVersionSchedule'

interface Props {
  employeeId: number
  selectedContractVersion: ContractVersionPageContractVersionFragment
  allContractVersions: ContractVersionPageContractVersionFragment[]
  hasContractVersionRequest: boolean
  onClickFix: () => void
  onClickDelete: () => void
}

const NewMenu = ({
  employeeId,
  selectedContractVersion,
  allContractVersions,
  onClickFix,
  onClickDelete,
  hasContractVersionRequest,
}: Props) => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const [showDeleteModal, setShowDeleteModal] = React.useState(false)

  const { id, _policies } = selectedContractVersion
  const canEdit = !!_policies?.edit?.can
  const canDelete = !!_policies?.delete?.can
  const contractStatus = getContractVersionSchedule(selectedContractVersion, allContractVersions)

  const [deleteContractVersion, { loading: deleting }] = useMutation(
    ContractDetailDeleteContractVersionDocument,
    {
      extractorFn: (data) => data.contractsMutations.deleteContractsContractVersion,
      refetchQueries: [EmployeeProfilePageDocument, ContractVersionsPageDocument],
      awaitRefetchQueries: true,
    }
  )

  const handleFix = () => {
    track(trackingEvents.employee.contracts.fixContractStart, { contract_status: contractStatus })
    onClickFix()
  }

  const handleDeleteClick = () => {
    if (!hasContractVersionRequest) setShowDeleteModal(true)
    onClickDelete()
  }

  const confirmDelete = async () => {
    setShowDeleteModal(false)
    if (!id) return
    const result = await deleteContractVersion({ variables: { id } })
    const handled = handleResult(result, () => {})
    if (handled.success) {
      navigate(navigation.employees.id.fullPath({ id: employeeId }))
      app.setFlash({
        type: 'correct',
        message: i18n.t('contract_versions.modals.delete.success'),
        time: 2000,
      })
      track(trackingEvents.employee.contracts.deleteContractConditions, {
        contract_status: contractStatus,
      })
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('contract_versions.modals.delete.error'),
        time: 2000,
      })
    }
  }

  const items = [
    canEdit && {
      label: i18n.t('contract_versions.header.fix_conditions'),
      onClick: handleFix,
    },
    canDelete && {
      label: i18n.t('contract_versions.navigator.cards.delete_version'),
      onClick: handleDeleteClick,
      variant: 'destructive' as const,
    },
  ].filter(Boolean)

  if (items.length === 0) return null

  return (
    <>
      <Dropdown items={items} />
      {showDeleteModal && (
        <Modal
          title={i18n.t('contract_versions.modals.delete.title')}
          description={i18n.t('contract_versions.modals.delete.description')}
          illustration="delete"
          onClose={() => setShowDeleteModal(false)}
          onSubmit={confirmDelete}
          footer={
            <>
              <Button type="secondary" onClick={() => setShowDeleteModal(false)}>
                {i18n.t('contract_versions.modals.delete.cancel')}
              </Button>
              <Button type="negative" submit disabled={deleting} loading={deleting}>
                {i18n.t('contract_versions.modals.delete.delete')}
              </Button>
            </>
          }
        />
      )}
    </>
  )
}

export default NewMenu
